/**
 * catalogLabels.ts
 *
 * The per-market override rule shared by every catalog field that carries a
 * canonical value plus a sparse per-market override map — labels, question
 * text, and description all share that wire shape by design, so they resolve
 * through one primitive rather than several that can drift.
 *
 * The canonical value is always the registry default, never a requested
 * market's, so the primary and the overrides share one reference frame and
 * `overrides[marketId] ?? canonical` is correct for every market including the
 * ones that override.
 *
 * Resolving across a SELECTION is by agreement, not by count — see
 * `groupByResolved`.
 */

/** The one override-resolution rule: overrides[marketId] ?? canonical. */
function resolveOverride(
  canonical: string | undefined,
  overrides: Record<string, string> | undefined,
  marketId: number | undefined,
): string | undefined {
  if (marketId == null) return canonical;
  return overrides?.[String(marketId)] ?? canonical;
}

/** A catalog node carrying a canonical label and optional per-market overrides. */
export interface LabelledCatalogNode {
  label: string;
  labelOverrides?: Record<string, string>;
}

/** The one label rule: labelOverrides[marketId] ?? label. */
export function resolveLabel(
  node: LabelledCatalogNode,
  marketId: number | undefined,
): string {
  return resolveOverride(node.label, node.labelOverrides, marketId) ?? node.label;
}

/** A catalog metric entry carrying question text and/or description. */
export interface PanelTextCatalogNode {
  questionText?: string;
  questionTextOverrides?: Record<string, string>;
  description?: string;
  descriptionOverrides?: Record<string, string>;
}

/**
 * The text shown beside the metric picker and in card tooltips, for ONE market:
 * the survey question wording first, falling back to the analyst-facing
 * description for composite/derived metrics that have no single underlying
 * question. `undefined` when the metric carries neither — callers render
 * nothing, never a placeholder.
 */
export function resolvePanelText(
  node: PanelTextCatalogNode,
  marketId: number | undefined,
): string | undefined {
  return (
    resolveOverride(node.questionText, node.questionTextOverrides, marketId) ??
    resolveOverride(node.description, node.descriptionOverrides, marketId)
  );
}

/**
 * Which branch `resolvePanelText` took: an actual survey question, or the
 * analyst-facing definition it falls back to for composite/derived metrics.
 */
export type PanelTextSource = "question" | "definition";

/**
 * Same resolution as `resolvePanelText`, but reports which branch produced
 * the value instead of discarding that fact — the UI wants to label the two
 * cases differently ("Question asked" vs "Definition"). `undefined` when the
 * metric carries neither, matching `resolvePanelText`.
 */
export function resolvePanelTextWithSource(
  node: PanelTextCatalogNode,
  marketId: number | undefined,
): { text: string; source: PanelTextSource } | undefined {
  const question = resolveOverride(node.questionText, node.questionTextOverrides, marketId);
  if (question !== undefined) return { text: question, source: "question" };

  const definition = resolveOverride(node.description, node.descriptionOverrides, marketId);
  if (definition !== undefined) return { text: definition, source: "definition" };

  return undefined;
}

/** One distinct resolved value, plus every selected market that produced it. */
export type MarketTextVariant = {
  marketIds: number[];
  text: string | undefined;
  /**
   * Provenance of `text` — "question" vs "definition" (see `PanelTextSource`).
   * Only `panelTextVariants` sets this; `labelVariants` groups plain labels,
   * which have no question/definition distinction to report.
   */
  source?: PanelTextSource;
};

/**
 * Group the selected markets by the value they resolve to, first-seen order.
 *
 * ONE variant means every selected market agrees, and its text can be shown as
 * the truth for the whole selection — this holds however many markets are
 * selected, which is the point: several Etsy markets that all say
 * "Favorability" agree, and falling back to a canonical string none of them
 * uses would be wrong. SEVERAL variants mean a genuine conflict, and the
 * per-market attribution is what a disclosure UI needs to explain it.
 *
 * An empty selection yields a single canonical variant.
 */
export function groupByResolved(
  resolve: (marketId: number | undefined) => string | undefined,
  marketIds: number[],
): MarketTextVariant[] {
  if (marketIds.length === 0) return [{ marketIds: [], text: resolve(undefined) }];

  const order: (string | undefined)[] = [];
  const grouped = new Map<string | undefined, number[]>();
  for (const id of marketIds) {
    const text = resolve(id);
    const existing = grouped.get(text);
    if (existing) {
      existing.push(id);
    } else {
      grouped.set(text, [id]);
      order.push(text);
    }
  }
  return order.map((text) => ({ marketIds: grouped.get(text)!, text }));
}

/** Per-market label variants across a selection. */
export function labelVariants(
  node: LabelledCatalogNode,
  marketIds: number[],
): MarketTextVariant[] {
  return groupByResolved((id) => resolveLabel(node, id), marketIds);
}

/**
 * Per-market question/description variants across a selection, each stamped
 * with its `source` so the UI can say "Question asked" vs "Definition"
 * instead of a single vague "wording". Grouping is still by resolved TEXT
 * (unchanged behaviour, and the only sound key — see `groupByResolved`); the
 * source can't itself split a group because every market that resolves to
 * the same text took the same branch to get there.
 */
export function panelTextVariants(
  node: PanelTextCatalogNode,
  marketIds: number[],
): MarketTextVariant[] {
  const variants = groupByResolved((id) => resolvePanelText(node, id), marketIds);
  return variants.map((variant) => ({
    ...variant,
    source: resolvePanelTextWithSource(node, variant.marketIds[0])?.source,
  }));
}

/**
 * The single source every variant agrees on, or `undefined` when they
 * disagree (a market defining a question alongside one with only a
 * description) or when no variant carries a source at all — the UI falls
 * back to neutral copy in both cases.
 */
export function panelTextSource(variants: MarketTextVariant[]): PanelTextSource | undefined {
  const first = variants[0]?.source;
  if (first === undefined) return undefined;
  return variants.every((v) => v.source === first) ? first : undefined;
}

/**
 * The value every selected market agreed on, or `undefined` when they
 * disagree. Note `undefined` is also what a unanimous "no text at all"
 * returns — both mean "nothing to show as the truth for this selection", and
 * callers that must distinguish them inspect `variants.length` directly.
 */
export function agreedValue(variants: MarketTextVariant[]): string | undefined {
  return variants.length === 1 ? variants[0].text : undefined;
}

/**
 * The market a label should resolve against, given the current selection.
 *
 * SUPERSEDED for the metric catalog by `labelVariants` + `agreedValue`, which
 * test whether the markets actually agree rather than merely counting them.
 * Retained for the distributions catalog / Profile mode, whose selectors still
 * take a single market id (see the 2026-08-06 metric-question-text-catalog
 * spec §11) — migrating those is tracked separately. Do not use it for new
 * metric-catalog code.
 */
export function labelMarketId(marketIds: number[]): number | undefined {
  return marketIds.length === 1 ? marketIds[0] : undefined;
}
